import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { Shell, type ShellUser } from './layouts/Shell';
import { useTranslation } from './i18n';

/* ---------- Lazy-loaded page stubs ---------- */

const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminTenants = lazy(() => import('./pages/admin/Tenants'));
const AdminTenantDetail = lazy(() => import('./pages/admin/TenantDetail'));
const AdminTenantWizard = lazy(() => import('./pages/admin/TenantWizardPage'));
const AdminTenantImport = lazy(() => import('./pages/admin/TenantImportPage'));
const AdminTenantConfig = lazy(() => import('./pages/admin/TenantConfig'));
const AdminOperator = lazy(() => import('./pages/admin/Operator'));
const AdminPolicy = lazy(() => import('./pages/admin/Policy'));
const AdminPolicyDetail = lazy(() => import('./pages/admin/PolicyDetail'));
const AdminComponents = lazy(() => import('./pages/admin/Components'));
const AdminUpgrades = lazy(() => import('./pages/admin/Upgrades'));
const AdminBackup = lazy(() => import('./pages/admin/Backup'));
const AdminMonitoring = lazy(() => import('./pages/admin/Monitoring'));
const AdminAudit = lazy(() => import('./pages/admin/Audit'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));

const TenantOverview = lazy(() => import('./pages/tenant/OverviewPage'));
const TenantConfig = lazy(() => import('./pages/tenant/ConfigPage'));
const TenantDeployments = lazy(() => import('./pages/tenant/DeploymentsPage'));
const TenantUsers = lazy(() => import('./pages/tenant/UsersPage'));
const TenantApps = lazy(() => import('./pages/tenant/AppsPage'));
const TenantIAM = lazy(() => import('./pages/tenant/IAMPage'));
const TenantBackup = lazy(() => import('./pages/tenant/BackupPage'));
const TenantAudit = lazy(() => import('./pages/tenant/AuditPage'));

/* ---------- Loading fallback ---------- */

/** Exported for the render test (typescript:S6819: native <output>). */
export function PageLoader() {
  const { t } = useTranslation();
  return (
    <output
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem',
        color: 'var(--color-text-secondary, #6b7280)',
        fontSize: '0.875rem',
      }}
      aria-label={t('common.pageLoading')}
    >
      {t('common.loading')}
    </output>
  );
}

function LazyPage({ children }: { readonly children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

/* ---------- Unauthorized page ---------- */

/** Exported for the axe render test (BITV 2.0 accessibility gate). */
export function Unauthorized() {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '1rem',
        color: 'var(--color-text)',
      }}
    >
      <h1 style={{ fontSize: '3rem', margin: 0 }}>403</h1>
      <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)' }}>
        {t('errors.accessDenied')}
      </p>
    </div>
  );
}

/* ---------- Route guard ---------- */

interface GuardProps {
  readonly allowedRole: 'platform-admin' | 'tenant-admin';
  readonly userRole: string;
  readonly children: React.ReactNode;
}

function RoleGuard({ allowedRole, userRole, children }: GuardProps) {
  if (userRole !== allowedRole) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
}

/* ---------- Router factory ---------- */

export function createAppRouter(user: ShellUser): ReturnType<typeof createBrowserRouter> {
  const adminRoutes: RouteObject[] = [
    { index: true, element: <Navigate to="dashboard" replace /> },
    { path: 'dashboard', element: <LazyPage><AdminDashboard /></LazyPage> },
    { path: 'tenants', element: <LazyPage><AdminTenants /></LazyPage> },
    { path: 'tenants/new', element: <LazyPage><AdminTenantWizard /></LazyPage> },
    { path: 'tenants/import', element: <LazyPage><AdminTenantImport /></LazyPage> },
    { path: 'tenants/:id', element: <LazyPage><AdminTenantDetail /></LazyPage> },
    { path: 'tenants/:id/config', element: <LazyPage><AdminTenantConfig /></LazyPage> },
    { path: 'operator', element: <LazyPage><AdminOperator /></LazyPage> },
    { path: 'policy', element: <LazyPage><AdminPolicy /></LazyPage> },
    { path: 'policy/:profile', element: <LazyPage><AdminPolicyDetail /></LazyPage> },
    { path: 'components', element: <LazyPage><AdminComponents /></LazyPage> },
    { path: 'upgrades', element: <LazyPage><AdminUpgrades /></LazyPage> },
    { path: 'backup', element: <LazyPage><AdminBackup /></LazyPage> },
    { path: 'monitoring', element: <LazyPage><AdminMonitoring /></LazyPage> },
    { path: 'audit', element: <LazyPage><AdminAudit /></LazyPage> },
    { path: 'settings', element: <LazyPage><AdminSettings /></LazyPage> },
  ];

  const tenantRoutes: RouteObject[] = [
    { index: true, element: <Navigate to="overview" replace /> },
    { path: 'overview', element: <LazyPage><TenantOverview /></LazyPage> },
    { path: 'config', element: <LazyPage><TenantConfig /></LazyPage> },
    { path: 'deployments', element: <LazyPage><TenantDeployments /></LazyPage> },
    { path: 'users', element: <LazyPage><TenantUsers /></LazyPage> },
    { path: 'apps', element: <LazyPage><TenantApps /></LazyPage> },
    { path: 'iam', element: <LazyPage><TenantIAM /></LazyPage> },
    { path: 'backup', element: <LazyPage><TenantBackup /></LazyPage> },
    { path: 'audit', element: <LazyPage><TenantAudit /></LazyPage> },
  ];

  const routes: RouteObject[] = [
    {
      element: <Shell user={user} />,
      children: [
        {
          path: 'admin/*',
          element: (
            <RoleGuard allowedRole="platform-admin" userRole={user.role}>
              <Navigate to="" replace />
            </RoleGuard>
          ),
        },
        {
          path: 'admin',
          children: adminRoutes.map((r) => ({
            ...r,
            element: (
              <RoleGuard allowedRole="platform-admin" userRole={user.role}>
                {r.element}
              </RoleGuard>
            ),
          })),
        },
        {
          path: 'tenant',
          children: tenantRoutes.map((r) => ({
            ...r,
            element: (
              <RoleGuard allowedRole="tenant-admin" userRole={user.role}>
                {r.element}
              </RoleGuard>
            ),
          })),
        },
        { path: 'unauthorized', element: <Unauthorized /> },
        {
          path: '/',
          element: (
            <Navigate
              to={user.role === 'platform-admin' ? '/admin/dashboard' : '/tenant/overview'}
              replace
            />
          ),
        },
        {
          path: '*',
          element: (
            <Navigate
              to={user.role === 'platform-admin' ? '/admin/dashboard' : '/tenant/overview'}
              replace
            />
          ),
        },
      ],
    },
  ];

  return createBrowserRouter(routes);
}
